import {observer} from 'mobx-react';
import {
  ChangeEvent,
  ClipboardEvent,
  Dispatch,
  Fragment,
  KeyboardEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useQueryClient} from 'react-query';
import {useApiCalls} from '@hooks/init-calls';
import {useStoreContext} from '@store/store-context';
import {Checkbox as BeamCheckbox} from '@ui/atoms/checkbox';
import {MagnifyingGlassIcon} from '@ui/atoms/icons';
import {ArrowHeadDownIcon} from '@ui/atoms/icons/arrow-head-down';
import {WorkspaceInviteController} from './workspace-invite-controller';
import {WorkspaceInviteRequestImpl} from './workspace-invite-request';
import {
  addInviteEmailTokens,
  buildWorkspaceInvitePayload,
  InviteEmailToken,
} from './workspace-invite-dialog.utils';
import * as S from './workspace-invite-dialog.styles';

type InviteStep = 'emails' | 'assignment' | 'review' | 'success';

interface WorkspaceInviteDialogProps {
  onClose: () => void;
}

const stepOrder: InviteStep[] = ['emails', 'assignment', 'review'];

const getName = (user: any) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
  user?.name ||
  user?.email ||
  'Workspace member';

const getInitials = (user: any) =>
  [user?.firstName, user?.lastName]
    .filter(Boolean)
    .map((name: string) => name.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'BM';

export const WorkspaceInviteDialog = observer(
  ({onClose}: WorkspaceInviteDialogProps) => {
    const {
      authStore: {auth},
      usersStore: {users},
      groupStore: {groups},
      groupTypeStore: {groupType},
      storeDashboardRoutes: {activateMemberTab},
    } = useStoreContext();
    const {initGroups, initGroupType, initUsers} = useApiCalls();
    const queryClient = useQueryClient();
    const csvInputRef = useRef<HTMLInputElement>(null);
    const managerOptionsRef = useRef<HTMLDivElement>(null);
    const groupListRef = useRef<HTMLDivElement>(null);

    const [step, setStep] = useState<InviteStep>('emails');
    const [emailTokens, setEmailTokens] = useState<InviteEmailToken[]>([]);
    const [emailDraft, setEmailDraft] = useState('');
    const [managerId, setManagerId] = useState(auth.user.id || '');
    const [groupIds, setGroupIds] = useState<string[]>([]);
    const [managerOpen, setManagerOpen] = useState(false);
    const [managerSearch, setManagerSearch] = useState('');
    const [groupSearch, setGroupSearch] = useState('');
    const [managerScrollIndicators, setManagerScrollIndicators] = useState({
      canScrollUp: false,
      canScrollDown: false,
    });
    const [groupScrollIndicators, setGroupScrollIndicators] = useState({
      canScrollUp: false,
      canScrollDown: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [csvFile, setCsvFile] = useState<{
      name: string;
      base64: string;
    } | null>(null);

    useEffect(() => {
      initGroups();
      initGroupType();
      initUsers();
    }, [initGroupType, initGroups, initUsers]);

    useEffect(() => {
      const closeOnEscape = (event: globalThis.KeyboardEvent) => {
        if (event.key === 'Escape') {
          if (managerOpen) setManagerOpen(false);
          else onClose();
        }
      };
      window.addEventListener('keydown', closeOnEscape);
      return () => window.removeEventListener('keydown', closeOnEscape);
    }, [managerOpen, onClose]);

    const activeUsers = useMemo(
      () =>
        (users || []).filter(
          (user: any) =>
            user?.id &&
            !user?.deletedAt &&
            user?.status !== 'deactivated' &&
            user?.status !== 'suspended' &&
            user?.emailVerified !== false,
        ),
      [users],
    );

    const selectedManager =
      activeUsers.find((user: any) => user.id === managerId) || auth.user;

    const filteredManagers = activeUsers.filter((user: any) =>
      `${getName(user)} ${user.email || ''}`
        .toLowerCase()
        .includes(managerSearch.toLowerCase()),
    );

    const filteredGroups = (groups || []).filter((group: any) =>
      group.name?.toLowerCase().includes(groupSearch.trim().toLowerCase()),
    );

    const selectedGroups = (groups || []).filter((group: any) =>
      groupIds.includes(group.id),
    );

    const updateScrollIndicators = useCallback(
      (
        element: HTMLDivElement | null,
        setIndicators: Dispatch<
          SetStateAction<{
            canScrollUp: boolean;
            canScrollDown: boolean;
          }>
        >,
      ) => {
        if (!element) {
          setIndicators({canScrollUp: false, canScrollDown: false});
          return;
        }
        setIndicators({
          canScrollUp: element.scrollTop > 2,
          canScrollDown:
            element.scrollTop + element.clientHeight < element.scrollHeight - 2,
        });
      },
      [],
    );

    const updateManagerScrollIndicators = useCallback(
      () =>
        updateScrollIndicators(
          managerOptionsRef.current,
          setManagerScrollIndicators,
        ),
      [updateScrollIndicators],
    );

    const updateGroupScrollIndicators = useCallback(
      () =>
        updateScrollIndicators(groupListRef.current, setGroupScrollIndicators),
      [updateScrollIndicators],
    );

    useEffect(() => {
      if (!managerOpen) return;
      if (managerOptionsRef.current) managerOptionsRef.current.scrollTop = 0;
      const frame = requestAnimationFrame(updateManagerScrollIndicators);
      return () => cancelAnimationFrame(frame);
    }, [
      filteredManagers.length,
      managerOpen,
      managerSearch,
      updateManagerScrollIndicators,
    ]);

    useEffect(() => {
      if (step !== 'assignment') return;
      if (groupListRef.current) groupListRef.current.scrollTop = 0;
      const frame = requestAnimationFrame(updateGroupScrollIndicators);
      return () => cancelAnimationFrame(frame);
    }, [filteredGroups.length, groupSearch, step, updateGroupScrollIndicators]);

    const groupTypeName = (group: any) => {
      if (typeof group?.groupType === 'object') return group.groupType.name;
      return (
        groupType.find((type: any) => type.id === group?.groupType)?.name ||
        'Group'
      );
    };

    const commitDraft = () => {
      if (!emailDraft.trim()) return;
      setEmailTokens((current) => addInviteEmailTokens(current, emailDraft));
      setEmailDraft('');
    };

    const handleEmailKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
        event.preventDefault();
        commitDraft();
      }
      if (event.key === 'Backspace' && !emailDraft && emailTokens.length > 0) {
        setEmailTokens((current) => current.slice(0, -1));
      }
    };

    const handleEmailPaste = (event: ClipboardEvent<HTMLInputElement>) => {
      const pasted = event.clipboardData.getData('text');
      if (/[,;\n\s]/.test(pasted.trim())) {
        event.preventDefault();
        setEmailTokens((current) => addInviteEmailTokens(current, pasted));
      }
    };

    const handleCsv = (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        setCsvFile({
          name: file.name,
          base64: result.replace('data:', '').replace(/^.+,/, ''),
        });
        setError('');
      };
      reader.readAsDataURL(file);
    };

    const goToAssignment = () => {
      const nextTokens = emailDraft.trim()
        ? addInviteEmailTokens(emailTokens, emailDraft)
        : emailTokens;
      setEmailTokens(nextTokens);
      setEmailDraft('');
      const hasInvalidEmail = nextTokens.some((token) => !token.valid);
      if (!nextTokens.length || hasInvalidEmail) return;
      setStep('assignment');
      setError('');
    };

    const finishInvitation = async () => {
      setSubmitting(true);
      setError('');
      const controller = new WorkspaceInviteController(
        new WorkspaceInviteRequestImpl(),
      );
      const response: any = csvFile
        ? await controller.newWorkspaceInviteCsv(
            {inviteFile: csvFile.base64},
            auth.tokens.access.token,
            {error: false, success: false},
          )
        : await controller.newWorkspaceInvite(
            buildWorkspaceInvitePayload(emailTokens, managerId, groupIds),
            auth.tokens.access.token,
            ['must be a valid email'],
            {error: false, success: false},
          );

      const failed =
        response?.status >= 400 ||
        response?.statusCode >= 400 ||
        response?.data?.statusCode >= 400;
      if (failed || !response) {
        setError(
          response?.data?.message ||
            response?.message ||
            'We could not send these invitations. Please try again.',
        );
        setSubmitting(false);
        return;
      }

      await Promise.all([
        queryClient.invalidateQueries('inviteMembers'),
        queryClient.invalidateQueries('workspace-member-counts'),
        queryClient.invalidateQueries('activeUsers'),
        queryClient.invalidateQueries('initGroupMembers'),
      ]);
      initUsers();
      setSubmitting(false);
      setStep('success');
    };

    const resetInvitation = () => {
      setStep('emails');
      setEmailTokens([]);
      setEmailDraft('');
      setManagerId(auth.user.id || '');
      setGroupIds([]);
      setCsvFile(null);
      setError('');
    };

    const closeAndShowInvites = () => {
      activateMemberTab('invite');
      onClose();
    };

    const currentIndex = stepOrder.indexOf(step);
    const pendingEmailTokens = emailDraft.trim()
      ? addInviteEmailTokens(emailTokens, emailDraft)
      : emailTokens;
    const canContinueFromEmails =
      !!pendingEmailTokens.length &&
      pendingEmailTokens.every((token) => token.valid);
    const canContinueFromAssignment = !!managerId && groupIds.length > 0;

    const renderStepper = () => (
      <S.Stepper aria-label="Invitation progress">
        {stepOrder.map((item, index) => {
          const state =
            currentIndex > index || step === 'success'
              ? 'complete'
              : currentIndex === index
              ? 'active'
              : 'idle';
          return (
            <Fragment key={item}>
              <S.StepPill $state={state}>
                {item === 'emails'
                  ? 'Emails'
                  : item === 'assignment'
                  ? 'Manager & Groups'
                  : 'Review'}
              </S.StepPill>
              {index < stepOrder.length - 1 && (
                <S.StepConnector $complete={currentIndex > index} />
              )}
            </Fragment>
          );
        })}
      </S.Stepper>
    );

    return (
      <S.DialogFrame
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-title">
        <S.DialogHeader>
          <S.TitleRow>
            <div>
              <S.DialogTitle id="invite-title">
                {step === 'success' ? 'Invitation sent!' : 'Invite people'}
              </S.DialogTitle>
              <S.DialogSubtitle>
                {step === 'success'
                  ? 'Your invitations are on their way.'
                  : 'Add people to your workspace and choose where they belong.'}
              </S.DialogSubtitle>
            </div>
            <S.CloseButton
              type="button"
              onClick={onClose}
              aria-label="Close invite dialog">
              <span aria-hidden="true">×</span> Close
            </S.CloseButton>
          </S.TitleRow>
          {step !== 'success' && renderStepper()}
        </S.DialogHeader>

        <S.DialogBody>
          {step === 'emails' && (
            <>
              <S.SectionTitle>Who would you like to invite?</S.SectionTitle>
              <S.SectionDescription>
                Add one or more email addresses.
              </S.SectionDescription>
              <S.FieldLabel htmlFor="invite-email-input">
                Email addresses
              </S.FieldLabel>
              <S.EmailInput $error={emailTokens.some((token) => !token.valid)}>
                {emailTokens.map((token) => (
                  <S.EmailChip key={token.value} $invalid={!token.valid}>
                    {token.value}
                    <S.ChipRemove
                      type="button"
                      aria-label={`Remove ${token.value}`}
                      onClick={() =>
                        setEmailTokens((current) =>
                          current.filter((item) => item.value !== token.value),
                        )
                      }>
                      ×
                    </S.ChipRemove>
                  </S.EmailChip>
                ))}
                <S.EmailDraftInput
                  id="invite-email-input"
                  value={emailDraft}
                  onChange={(event) => setEmailDraft(event.target.value)}
                  onKeyDown={handleEmailKeyDown}
                  onPaste={handleEmailPaste}
                  onBlur={commitDraft}
                  placeholder={emailTokens.length ? '' : 'name@company.com'}
                />
              </S.EmailInput>
              <S.HelperRow>
                <span>
                  {emailTokens.some((token) => !token.valid)
                    ? 'Correct or remove the highlighted email address.'
                    : 'Separate multiple emails with Enter or a comma.'}
                </span>
                <span>{emailTokens.length} people</span>
              </S.HelperRow>

              <S.OrDivider>or</S.OrDivider>
              <input
                ref={csvInputRef}
                hidden
                type="file"
                accept=".csv,text/csv"
                onChange={handleCsv}
              />
              <S.CsvCard
                type="button"
                onClick={() => csvInputRef.current?.click()}>
                <span>
                  <S.CsvTitle>
                    {csvFile ? csvFile.name : 'Paste a list'}
                  </S.CsvTitle>
                  <S.CsvDescription>
                    {csvFile
                      ? 'CSV ready to send. Choose the file again to replace it.'
                      : 'Upload a CSV when you already have a prepared invite list.'}
                  </S.CsvDescription>
                </span>
                <span aria-hidden="true">＋</span>
              </S.CsvCard>
              {error && <S.ErrorBanner role="alert">{error}</S.ErrorBanner>}
            </>
          )}

          {step === 'assignment' && (
            <>
              <S.SummaryCard>
                <S.SummaryText>
                  {emailTokens.length} email
                  {emailTokens.length === 1 ? '' : 's'} added
                </S.SummaryText>
                <S.TextButton type="button" onClick={() => setStep('emails')}>
                  Edit
                </S.TextButton>
              </S.SummaryCard>
              <S.AssignmentGrid>
                <div>
                  <S.FieldLabel>Manager</S.FieldLabel>
                  <S.DropdownWrap>
                    <S.SelectTrigger
                      type="button"
                      $open={managerOpen}
                      onClick={() => setManagerOpen((open) => !open)}>
                      <S.Person>
                        <S.Initials>{getInitials(selectedManager)}</S.Initials>
                        <S.PersonText>
                          <S.PersonName>
                            {getName(selectedManager)}
                          </S.PersonName>
                          <S.PersonRole>
                            {selectedManager?.jobTitle || 'Workspace member'}
                          </S.PersonRole>
                        </S.PersonText>
                      </S.Person>
                      <ArrowHeadDownIcon />
                    </S.SelectTrigger>
                    {managerOpen && (
                      <S.DropdownPanel>
                        <S.SearchBox>
                          <input
                            autoFocus
                            value={managerSearch}
                            onChange={(event) =>
                              setManagerSearch(event.target.value)
                            }
                            placeholder="Search managers"
                          />
                          <MagnifyingGlassIcon />
                        </S.SearchBox>
                        <S.ListViewport>
                          {managerScrollIndicators.canScrollUp && (
                            <S.ListFade $position="top" aria-hidden="true" />
                          )}
                          <S.OptionList
                            ref={managerOptionsRef}
                            onScroll={updateManagerScrollIndicators}>
                            {filteredManagers.map((user: any) => (
                              <S.OptionButton
                                key={user.id}
                                type="button"
                                $selected={user.id === managerId}
                                onClick={() => {
                                  setManagerId(user.id);
                                  setManagerOpen(false);
                                }}>
                                <S.Person>
                                  <S.Initials>{getInitials(user)}</S.Initials>
                                  <S.PersonText>
                                    <S.PersonName>{getName(user)}</S.PersonName>
                                    <S.PersonRole>
                                      {user.jobTitle || 'Workspace member'}
                                    </S.PersonRole>
                                  </S.PersonText>
                                </S.Person>
                              </S.OptionButton>
                            ))}
                          </S.OptionList>
                          {managerScrollIndicators.canScrollDown && (
                            <S.ListFade $position="bottom" aria-hidden="true" />
                          )}
                        </S.ListViewport>
                      </S.DropdownPanel>
                    )}
                  </S.DropdownWrap>
                </div>

                <S.GroupSection>
                  <S.GroupSectionTitle>Assign to group(s)</S.GroupSectionTitle>
                  <S.GroupSectionDescription>
                    Members can view and update their own goals and check-ins.
                  </S.GroupSectionDescription>
                  <S.GroupSearchBox>
                    <input
                      id="invite-group-search"
                      value={groupSearch}
                      onChange={(event) => setGroupSearch(event.target.value)}
                      placeholder="Search groups"
                      aria-label="Search groups"
                    />
                    <MagnifyingGlassIcon />
                  </S.GroupSearchBox>
                  <S.ListViewport>
                    {groupScrollIndicators.canScrollUp && (
                      <S.GroupListFade $position="top" aria-hidden="true" />
                    )}
                    <S.GroupList
                      ref={groupListRef}
                      onScroll={updateGroupScrollIndicators}>
                      {filteredGroups.map((group: any) => {
                        const selected = groupIds.includes(group.id);
                        return (
                          <S.GroupRow
                            key={group.id}
                            $selected={selected}
                            htmlFor={`invite-group-${group.id}`}>
                            <BeamCheckbox
                              id={`invite-group-${group.id}`}
                              boxSize="small"
                              checked={selected}
                              aria-label={`Select ${group.name}`}
                              onChange={() =>
                                setGroupIds((current) =>
                                  selected
                                    ? current.filter((id) => id !== group.id)
                                    : [...current, group.id],
                                )
                              }
                            />
                            <S.GroupCopy>
                              <S.GroupName>{group.name}</S.GroupName>
                              {Number.isFinite(group.membersCount) && (
                                <S.GroupMeta>
                                  {group.membersCount} member
                                  {group.membersCount === 1 ? '' : 's'}
                                </S.GroupMeta>
                              )}
                            </S.GroupCopy>
                            <S.TypePill>{groupTypeName(group)}</S.TypePill>
                          </S.GroupRow>
                        );
                      })}
                    </S.GroupList>
                    {groupScrollIndicators.canScrollDown && (
                      <S.GroupListFade $position="bottom" aria-hidden="true" />
                    )}
                  </S.ListViewport>
                </S.GroupSection>
              </S.AssignmentGrid>
            </>
          )}

          {step === 'review' && (
            <>
              <S.SectionTitle>Review invitations</S.SectionTitle>
              <S.SectionDescription>
                Check the details below before sending the invitations.
              </S.SectionDescription>
              <S.ReviewList>
                {emailTokens.map((token) => (
                  <S.ReviewRow key={token.value}>
                    <S.EmailPill title={token.value}>{token.value}</S.EmailPill>
                    <S.GroupPills>
                      {selectedGroups.slice(0, 2).map((group: any) => (
                        <S.GroupPill key={group.id}>{group.name}</S.GroupPill>
                      ))}
                      {selectedGroups.length > 2 && (
                        <S.GroupPill
                          title={selectedGroups
                            .slice(2)
                            .map((group: any) => group.name)
                            .join(', ')}>
                          +{selectedGroups.length - 2}
                        </S.GroupPill>
                      )}
                    </S.GroupPills>
                    <S.Person>
                      <S.Initials>{getInitials(selectedManager)}</S.Initials>
                      <S.PersonText>
                        <S.PersonName>{getName(selectedManager)}</S.PersonName>
                        <S.PersonRole>Manager</S.PersonRole>
                      </S.PersonText>
                    </S.Person>
                  </S.ReviewRow>
                ))}
              </S.ReviewList>
              {error && <S.ErrorBanner role="alert">{error}</S.ErrorBanner>}
            </>
          )}

          {step === 'success' && (
            <S.SuccessBody>
              <S.SectionTitle>
                {csvFile
                  ? 'Your invitation list has been sent.'
                  : `${emailTokens.length} invitation${
                      emailTokens.length === 1 ? '' : 's'
                    } sent`}
              </S.SectionTitle>
              <S.SectionDescription>
                Invited people will appear under the Invited tab until they
                join.
              </S.SectionDescription>
              <S.SuccessList>
                {(csvFile
                  ? [csvFile.name]
                  : emailTokens.map((token) => token.value)
                ).map((value) => (
                  <S.SuccessRow key={value}>
                    <span>{value}</span>
                    <S.SentStatus>Sent</S.SentStatus>
                  </S.SuccessRow>
                ))}
              </S.SuccessList>
            </S.SuccessBody>
          )}
        </S.DialogBody>

        <S.DialogFooter>
          {step === 'emails' && (
            <>
              <S.SecondaryButton type="button" onClick={onClose}>
                Cancel
              </S.SecondaryButton>
              <S.PrimaryButton
                type="button"
                disabled={!canContinueFromEmails && !csvFile}
                onClick={csvFile ? finishInvitation : goToAssignment}>
                {csvFile
                  ? submitting
                    ? 'Sending…'
                    : 'Send invitations'
                  : 'Next'}
              </S.PrimaryButton>
            </>
          )}
          {step === 'assignment' && (
            <>
              <S.SecondaryButton
                type="button"
                onClick={() => setStep('emails')}>
                Go back
              </S.SecondaryButton>
              <S.PrimaryButton
                type="button"
                disabled={!canContinueFromAssignment}
                onClick={() => setStep('review')}>
                Next
              </S.PrimaryButton>
            </>
          )}
          {step === 'review' && (
            <>
              <S.SecondaryButton
                type="button"
                onClick={() => setStep('assignment')}>
                Go back
              </S.SecondaryButton>
              <S.PrimaryButton
                type="button"
                disabled={submitting}
                onClick={finishInvitation}>
                {submitting ? 'Sending…' : 'Send invitations'}
              </S.PrimaryButton>
            </>
          )}
          {step === 'success' && (
            <>
              <S.SecondaryButton type="button" onClick={resetInvitation}>
                Invite more people
              </S.SecondaryButton>
              <S.PrimaryButton type="button" onClick={closeAndShowInvites}>
                Done
              </S.PrimaryButton>
            </>
          )}
        </S.DialogFooter>
      </S.DialogFrame>
    );
  },
);
